import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { visibleInterval } from '@/lib/polling';
import { cn, humanize, timeOnly } from '@/lib/utils';
import { DataTable, PageHeader, StatTile } from '@/components/ui/layout';
import { Alert, Badge, Button } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ErCaseDialog } from './ErCaseDialog';
import { TriageChip } from './TriageChip';
import { vitalsSummary, type ErBoardResponse, type ErBoardRow } from './er-board';

const BOARD_LIMIT = 200;
const BOARD_POLL_MS = 60_000;

/**
 * The ER board (SRS 6.5).
 *
 * Ordered sickest-first, then longest-waiting, which is how a charge nurse
 * reads the department. Untriaged arrivals are called out at the top: a patient
 * nobody has assessed is the one genuine alarm on this screen.
 */
export function ErBoardPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [viewing, setViewing] = useState<string | null>(params.get('case'));
  const toast = useToast();
  const queryClient = useQueryClient();

  const board = useQuery({
    queryKey: ['er-board'],
    queryFn: () => get<ErBoardResponse>(`/er/board${qs({ limit: BOARD_LIMIT })}`),
    refetchInterval: visibleInterval(BOARD_POLL_MS),
  });

  const seen = useMutation({
    mutationFn: (id: string) => post(`/er/cases/${id}/seen`),
    onSuccess: () => {
      toast.success(tr('opd', 'er_door_to_doctor_recorded'));
      void queryClient.invalidateQueries({ queryKey: ['er-board'] });
    },
    onError: (error) => toast.error(tr('opd', 'er_could_not_record_it'), describeError(error)),
  });

  const cases = board.data?.data ?? [];
  const moreOnTheBoard = Boolean(board.data?.nextCursor);
  const untriaged = cases.filter((c) => c.needsTriage);

  return (
    <>
      <PageHeader
        title={tr('opd', 'er_board_title')}
        description={tr('opd', 'er_board_description')}
        actions={can('er.manage') && <Button onClick={() => navigate('/er/new')}>{tr('opd', 'er_register_arrival')}</Button>}
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label={tr('opd', 'er_in_the_department')} value={cases.length} />
        <StatTile label={tr('opd', 'er_awaiting_triage')} value={untriaged.length} tone={untriaged.length ? 'destructive' : 'default'} />
        <StatTile label={tr('opd', 'er_awaiting_doctor')} value={cases.filter((c) => c.needsDoctor).length} tone="warning" />
        <StatTile
          label={tr('opd', 'er_longest_wait')}
          value={cases.length ? `${Math.max(...cases.map((c) => c.waitingMinutes))} ${tr('opd', 'min')}` : '—'}
        />
      </div>

      {untriaged.length > 0 && (
        <Alert variant="destructive" className="mb-4">
          <span className="inline-flex items-center gap-1 font-semibold">
            <AlertTriangle className="h-4 w-4" /> {tr('opd', 'er_untriaged_arrivals', { count: untriaged.length })}
          </span>
        </Alert>
      )}

      {moreOnTheBoard && (
        <Alert variant="warning" className="mb-4">
          {tr('opd', 'er_board_truncated', { limit: BOARD_LIMIT })}
        </Alert>
      )}

      <DataTable
        rows={cases}
        loading={board.isLoading} refreshing={board.isFetching}
        error={board.error}
        rowKey={(row) => row.id}
        empty={tr('opd', 'er_department_empty')}
        columns={[
          { header: tr('opd', 'triage'), width: '150px', sort: (row) => row.triageLevel, cell: (row) => <TriageChip level={row.triageLevel} /> },
          {
            header: tr('opd', 'patient'),
            sort: (row) => row.patient?.fullName ?? row.unknownPatientLabel,
            cell: (row) => (
              <div>
                <p className="font-medium">
                  {row.patient?.fullName ?? row.unknownPatientLabel ?? tr('opd', 'er_unidentified')}
                  {row.isMlc && (
                    <Badge variant="destructive" className="ms-1">
                      {tr('opd', 'er_mlc')}
                    </Badge>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {row.number}
                  {` · ${row.patient ? row.patient.mrn : tr('opd', 'er_not_identified')}`}
                </p>
              </div>
            ),
          },
          { header: tr('opd', 'complaint'), sort: (row) => row.chiefComplaint, cell: (row) => row.chiefComplaint ?? '—' },
          {
            header: tr('opd', 'doctor'),
            sort: (row) => row.attendingDoctor?.name,
            cell: (row) =>
              row.attendingDoctor ? (
                <span title={row.attendingDoctor.designation ?? undefined}>{row.attendingDoctor.name}</span>
              ) : (
                <span className="text-muted-foreground">{row.needsDoctor ? tr('opd', 'er_awaiting_doctor') : '—'}</span>
              ),
          },
          {
            header: tr('opd', 'vitals_2'),
            cell: (row) => <VitalsCell vitals={row.lastVitals} />,
          },
          { header: tr('opd', 'arrival'), sort: (row) => row.arrivalMode, cell: (row) => <Badge variant="secondary">{humanize(row.arrivalMode)}</Badge> },
          { header: tr('opd', 'er_bay'), sort: (row) => row.bayLabel, cell: (row) => row.bayLabel ?? '—' },
          {
            header: tr('opd', 'waiting'),
            align: 'right',
            sort: (row) => row.waitingMinutes,
            cell: (row) => (
              <span className={cn('tabular', row.waitingMinutes > 60 && 'font-semibold text-destructive')}>
                {row.waitingMinutes} {tr('opd', 'min')}
              </span>
            ),
          },
          { header: tr('opd', 'arrived'), sort: (row) => row.arrivedAt, cell: (row) => timeOnly(row.arrivedAt) },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-1">
                <Button size="sm" variant="ghost" onClick={() => setViewing(row.id)}>
                  {tr('opd', 'details')}
                </Button>
                {can('er.manage') && row.needsTriage && (
                  <Button size="sm" onClick={() => navigate(`/er/${row.id}/triage`)}>
                    {tr('opd', 'triage')}
                  </Button>
                )}
                {can('er.manage') && row.needsDoctor && (
                  <Button size="sm" variant="outline" onClick={() => seen.mutate(row.id)}>
                    {tr('opd', 'seen')}
                  </Button>
                )}
                {can('er.manage') && !row.needsTriage && (
                  <Button size="sm" variant="outline" onClick={() => navigate(`/er/${row.id}/disposition`)}>
                    {tr('opd', 'er_dispose')}
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      {viewing !== null && (
        <ErCaseDialog erCaseId={viewing} onClose={() => setViewing(null)} />
      )}
    </>
  );
}

function VitalsCell({ vitals }: { vitals: ErBoardRow['lastVitals'] }) {
  const parts = vitals ? vitalsSummary(vitals) : [];
  if (!vitals || parts.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span
      className={cn('tabular whitespace-nowrap text-xs', vitals.isAbnormal && 'font-semibold text-destructive')}
      title={tr('opd', vitals.isAbnormal ? 'er_recorded_x_abnormal' : 'er_recorded_x', { time: timeOnly(vitals.recordedAt) })}
    >
      {parts.join(' · ')}
    </span>
  );
}
